import styled from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { ANIM, ART_STYLE, COLORS, REDUCED_MOTION, SPACE } from '../../../../theme/tokens';

/* The open section darkens slightly; the background reaches a little past the content (negative margin, matching
 * padding) so the headers stay aligned with the column's title. */
export const Section = styled.section<{ $open: boolean }>`
  position: relative;
  margin: 0 -${SPACE[12]};
  padding: 0 ${SPACE[12]};
  border-radius: ${({ $open }) => ($open ? '14px' : '0')};
  background: ${({ $open }) => ($open ? COLORS.ingredientOpenBg : 'none')};
  transition:
    background ${ART_STYLE.ACCORDION_MS}ms ${ANIM.EASE},
    border-radius ${ART_STYLE.ACCORDION_MS}ms ${ANIM.EASE};

  /* Each section draws the divider above it: an inset line, not a border, stopping short of the open section's 14px
   * rounded corners. The last one has none below it (the Extract footer's own border closes the stack). */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${SPACE[16]};
    right: ${SPACE[16]};
    height: 1px;
    background: ${COLORS.hairline};
    pointer-events: none;
    transition: opacity ${ART_STYLE.ACCORDION_MS}ms ${ANIM.EASE};
  }

  /* No divider touches the open section: the one above it and the one below it fade out. The open and closed
   * sections carry different generated classes (their styles differ), so the one below is matched by the open
   * section's attribute, not by & twice (which only ever matched an open section after an open one). */
  &[data-open='true']::before,
  section[data-open='true'] + &::before {
    opacity: 0;
  }

  ${REDUCED_MOTION} {
    transition: none;

    &::before {
      transition: none;
    }
  }
`;

export const Header = styled.button`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  width: 100%;
  padding: ${SPACE[16]} 0;
  border: none;
  background: none;
  color: ${COLORS.textBright};
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
    border-radius: 10px;
  }
`;

export const HeaderIcon = styled.span<{ $open: boolean }>`
  flex: none;
  display: inline-flex;
  color: ${({ $open }) => ($open ? COLORS.ctaSolid : COLORS.textMuted)};
  transition: color ${ART_STYLE.ACCORDION_MS}ms ${ANIM.EASE};
`;

export const Title = styled(Text)`
  flex: none;
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.textBright};
`;

/** "(Optional)" after an optional section's title, shown while it is open (fades in as it opens). */
export const OptionalTag = styled(Text)<{ $open: boolean }>`
  flex: none;
  /* Reads as the title's continuation: closer to it than the header's 12px gap. */
  margin-left: -${SPACE[4]};
  font-size: 12.5px;
  color: ${COLORS.textFaint};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity ${ART_STYLE.ACCORDION_MS}ms ${ANIM.EASE};
`;

/** What the section holds, shown while it is closed (fades out as it opens). */
export const Summary = styled(Text)<{ $open: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
  font-size: 12.5px;
  color: ${COLORS.textFaint};
  opacity: ${({ $open }) => ($open ? 0 : 1)};
  transition: opacity ${ART_STYLE.ACCORDION_MS}ms ${ANIM.EASE};
`;

/** A round chevron that turns as the section opens; it brightens on hover. */
export const Chevron = styled.span<{ $open: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: auto;
  border-radius: 999px;
  border: 1px solid ${({ $open }) => ($open ? COLORS.ctaSolid : COLORS.hairline)};
  background: ${COLORS.surfaceFaint};
  color: ${({ $open }) => ($open ? COLORS.textBright : COLORS.textMuted)};
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
  transition:
    transform ${ART_STYLE.ACCORDION_MS}ms ${ANIM.EASE},
    border-color ${ART_STYLE.ACCORDION_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  ${Header}:hover & {
    color: ${COLORS.textBright};
    background: ${COLORS.surfaceMuted};
  }

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/* The body grows from 0fr to 1fr rows: a height animation with nothing to measure. */
export const Body = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  transition: grid-template-rows ${ART_STYLE.ACCORDION_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const BodyClip = styled.div`
  min-height: 0;
  overflow: hidden;
`;

export const BodyContent = styled.div<{ $open: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  /* 2px keeps the inner fields' focus rings clear of the clip. */
  padding: ${SPACE[2]} ${SPACE[2]} ${SPACE[20]};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: translateY(${({ $open }) => ($open ? '0' : `-${SPACE[8]}`)});
  transition:
    opacity ${ART_STYLE.ACCORDION_MS}ms ${ANIM.EASE},
    transform ${ART_STYLE.ACCORDION_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transform: none;
    transition: none;
  }
`;

export const SectionHint = styled(Text)`
  font-size: 12.5px;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;
